import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import { getFullName } from '../../../../../lib/personDetails.es6'
import { readPolicyField } from '../../../../../lib/policyDetails.es6'
import { getPartnerGuid } from '../../../../../lib/access.es6'

// A checkout link is only meaningful while the policy can still be paid for.
const CHECKOUT_DISALLOWED_STATUSES = ['Cancelled', 'Discarded']

const findPrincipal = policy => {
  const scheduleEntries =
    policy.quotation && Array.isArray(policy.quotation.policy_schedule_entries)
      ? policy.quotation.policy_schedule_entries
      : []
  const principalEntry = scheduleEntries.find(
    entry => entry.dependant && entry.dependant.relationship === 'Principal'
  )
  return (principalEntry && principalEntry.dependant) || {}
}

export default function GeneratePaymentLink(props) {
  const [checkoutDialogOpen, setCheckoutDialogOpen] = React.useState(false)
  const [checkoutLinkCopied, setCheckoutLinkCopied] = React.useState(false)
  const openedCheckoutUrl = React.useRef(null)

  // The checkout URL arrives via redux state, not a callback — open it the
  // moment it changes rather than on every render (it stays in state until
  // the dialog is closed/reset).
  React.useEffect(() => {
    if (
      props.checkoutLinkUrl &&
      props.checkoutLinkUrl !== openedCheckoutUrl.current
    ) {
      openedCheckoutUrl.current = props.checkoutLinkUrl
      setCheckoutDialogOpen(true)
      window.open(props.checkoutLinkUrl, '_blank', 'noopener,noreferrer')
    }
  }, [props.checkoutLinkUrl])

  if (!props.policy || getPartnerGuid() !== 'omk') {
    return null
  }

  const policy = props.policy
  const ad = policy.additional_details || {}
  const principal = findPrincipal(policy)
  // The backend matches customer_reference against an existing customer
  // record — the dependant/schedule-entry msisdn captured on the quotation
  // snapshot can differ from it, so the registered customer is preferred.
  const registeredCustomer =
    (props.customer && props.customer.currentCustomer) || {}

  const closeCheckoutDialog = () => {
    setCheckoutDialogOpen(false)
    setCheckoutLinkCopied(false)
    openedCheckoutUrl.current = null
    if (props.resetPolicyCheckoutLink) {
      props.resetPolicyCheckoutLink()
    }
  }

  const copyCheckoutLink = () => {
    if (!props.checkoutLinkUrl) return
    try {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(props.checkoutLinkUrl)
      } else {
        const ta = document.createElement('textarea')
        ta.value = props.checkoutLinkUrl
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCheckoutLinkCopied(true)
      setTimeout(() => setCheckoutLinkCopied(false), 1500)
    } catch (e) {
      // clipboard not available
    }
  }

  const generateCheckoutLink = () => {
    if (!props.generatePolicyCheckoutLink) return
    const fullName =
      getFullName(registeredCustomer) ||
      getFullName(principal) ||
      ad.principal_full_names ||
      ''
    const nameParts = fullName
      .trim()
      .split(/\s+/)
      .filter(Boolean)
    const amountInCents =
      (policy.quotation && policy.quotation.total_premium_amount_in_cents) ||
      (ad.premium_amount || 0) * 100
    props.generatePolicyCheckoutLink({
      policy_guid: policy.guid,
      amount_in_cents: amountInCents,
      partner: getPartnerGuid(),
      type: 'strike_money',
      customer_reference:
        registeredCustomer.msisdn ||
        principal.msisdn ||
        policy.beneficiary_msisdn ||
        '',
      additional_details: {
        email:
          registeredCustomer.email || readPolicyField(policy, 'email') || '',
        firstname: registeredCustomer.first_name || nameParts[0] || '',
        surname:
          registeredCustomer.last_name ||
          registeredCustomer.surname ||
          nameParts.slice(1).join(' ')
      }
    })
  }

  if (CHECKOUT_DISALLOWED_STATUSES.includes(policy.status)) {
    return null
  }

  return (
    <>
      <ButtonInc
        onClick={generateCheckoutLink}
        disabled={props.checkoutLinkLoader}
        style={{
          border: '1px solid #FBA92D',
          borderRadius: '8px',
          background: '#fff',
          padding: '5px 35px',
          minWidth: '210px',
          height: '40px'
        }}
      >
        {props.checkoutLinkLoader ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} sx={{ color: '#FBA92D' }} />
            <span style={{ color: '#000000' }}>Generating…</span>
          </Box>
        ) : (
          <span style={{ color: '#000000' }}>Generate Payment Link</span>
        )}
      </ButtonInc>

      <Dialog
        open={checkoutDialogOpen || !!props.checkoutLinkError}
        onClose={closeCheckoutDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {props.checkoutLinkError
            ? 'Payment link failed'
            : 'Payment link ready'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {props.checkoutLinkError ? (
            <Alert severity="error">
              {props.checkoutLinkError.message ||
                "We couldn't generate a payment link. Please try again."}
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                A checkout link has been opened in a new tab for the customer to
                complete payment. If it didn't open, use the link below.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={props.checkoutLinkUrl || ''}
                  InputProps={{ readOnly: true }}
                />
                <Button variant="outlined" onClick={copyCheckoutLink}>
                  {checkoutLinkCopied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {props.checkoutLinkError && (
            <Button
              onClick={() => {
                setCheckoutDialogOpen(false)
                generateCheckoutLink()
              }}
              variant="contained"
            >
              Retry
            </Button>
          )}
          {!props.checkoutLinkError && props.checkoutLinkUrl && (
            <Button
              onClick={() =>
                window.open(
                  props.checkoutLinkUrl,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              variant="contained"
            >
              Open Checkout
            </Button>
          )}
          <Button onClick={closeCheckoutDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
